import care_icon from '~/assets/images/care_icon.svg'
import coverage_icon from '~/assets/images/coverage_icon.svg'
import { Badge, Chip } from '@mui/material'
import { ONBOARDING_STATES } from '~/entities/person'
import {
  ProgramsPeopleEnrollmentListResponse,
  UserCoverageStatusesListResponse,
} from '@fireflyhealth/api'

import { differenceInDays, format, parse } from 'date-fns'
import { ISO_DATE_FORMAT } from '~/shared/lib/date'
import { IPatientInsuranceDetails } from '~/shared/types'
import {
  BENEFIT_PROGRAM_UID,
  DISCHARGE_IN_PROGRESS_TEXT,
  PRIMARY_CARE_PROGRAM_UID,
  PRIMARY_CARE_STATUS,
} from '~/entities/program'

// Type-checked against the API-generated enum — build will fail if the API removes these values.
type CoverageStatus = UserCoverageStatusesListResponse[number]['status']
const COVERED: CoverageStatus = 'covered'
const PROCESSING: CoverageStatus = 'processing'

interface PrimaryCareChipProps {
  onboardingStatus: string
  coverage: {
    status: string
    reason?: string | null
    name: string
    display_label: string
  } | null
  patientInsuranceInfo: IPatientInsuranceDetails | null
  programEnrollments?: ProgramsPeopleEnrollmentListResponse
}
const CareIcon = (
  <img
    src={care_icon}
    alt="Primary Care"
    style={{ width: 'auto', height: '1.3rem', marginLeft: '1rem' }}
  />
)

const CoverageIcon = (
  <img
    src={coverage_icon}
    alt="Coverage"
    style={{ width: 'auto', height: '1.3rem', marginLeft: '1rem' }}
  />
)

// Displays info about the Primary Care program
const PrimaryCareChip = (props: PrimaryCareChipProps) => {
  const {
    coverage,
    onboardingStatus,
    patientInsuranceInfo,
    programEnrollments: providedProgramEnrollments,
  } = props
  const insuranceState = coverage?.status

  // Create program enrollment map from provided data or fetched data
  const programEnrollmentMap = new Map<string, ProgramsPeopleEnrollmentListResponse>()

  if (providedProgramEnrollments) {
    // Process provided enrollments
    for (const programEnrollment of providedProgramEnrollments) {
      let enrollments: ProgramsPeopleEnrollmentListResponse = []
      if (programEnrollmentMap.has(programEnrollment.program)) {
        enrollments = programEnrollmentMap.get(programEnrollment.program) ?? []
      }
      enrollments.push(programEnrollment)
      programEnrollmentMap.set(programEnrollment.program, enrollments)
    }
  }

  const PrimaryCareEnrollments = programEnrollmentMap.get(PRIMARY_CARE_PROGRAM_UID)
  const BenefitProgramEnrollments = programEnrollmentMap.get(BENEFIT_PROGRAM_UID)
  const CurrentPrimaryCareEnrollment = PrimaryCareEnrollments
    ? PrimaryCareEnrollments[PrimaryCareEnrollments.length - 1]
    : null
  const CurrentBenefitProgramEnrollment = BenefitProgramEnrollments
    ? BenefitProgramEnrollments[BenefitProgramEnrollments.length - 1]
    : null

  if (
    (CurrentPrimaryCareEnrollment == null && CurrentBenefitProgramEnrollment == null) ||
    !patientInsuranceInfo
  )
    return null
  const insurancePayerName = patientInsuranceInfo.insurancePayerShortName
    ? patientInsuranceInfo.insurancePayerShortName
    : patientInsuranceInfo.insurancePayerName
  // Parse dates as date-only strings to avoid timezone conversion issues
  const insuranceCoverageStart = patientInsuranceInfo.coverageStartDate
    ? parse(patientInsuranceInfo.coverageStartDate, ISO_DATE_FORMAT, new Date())
    : null
  const insuranceCoverageEnd = patientInsuranceInfo.coverageEndDate
    ? parse(patientInsuranceInfo.coverageEndDate, ISO_DATE_FORMAT, new Date())
    : null
  const coverageEndDifference = insuranceCoverageEnd
    ? differenceInDays(insuranceCoverageEnd, new Date())
    : 0
  const coverageEndBadge = coverageEndDifference > 0 && coverageEndDifference <= 30 ? false : true

  const careChipVariant =
    (() => {
      if (
        CurrentPrimaryCareEnrollment !== null &&
        CurrentPrimaryCareEnrollment?.status !== PRIMARY_CARE_STATUS.CHURNED &&
        onboardingStatus === ONBOARDING_STATES.signedup
      ) {
        return 'outlined'
      } else return 'filled'
    })() || 'filled'

  const coverageChipVariant =
    (() => {
      if (COVERED === insuranceState) {
        return 'outlined'
      } else return 'filled'
    })() || 'filled'

  const coverageEndTooltip =
    (() => {
      if (coverageEndBadge == false) {
        // Unreachable with a null end date: coverageEndDifference would be 0, which
        // sets coverageEndBadge true.
        if (!insuranceCoverageEnd) return ''
        return ' ends on ' + format(insuranceCoverageEnd, 'MMM. dd, yyyy')
      } else return ''
    })() || ''

  // Insurance coverage state to a readable status.
  // display_label is the canonical label from the API (e.g. "Incomplete", "Expired", "Eligible").
  // Special cases override it for covered (show payer name) and processing (show "None" for non-active).
  const coverageStatus =
    (() => {
      switch (true) {
        case COVERED === insuranceState:
          if (insuranceCoverageStart != null && insuranceCoverageStart > new Date()) {
            return insurancePayerName + ' starts ' + format(insuranceCoverageStart, 'MMM. dd, yyyy')
          } else {
            return insurancePayerName
          }
        case PROCESSING === insuranceState &&
          [ONBOARDING_STATES.signedup].includes(onboardingStatus) == false:
          return 'None'
        default:
          return coverage?.display_label
      }
    })() || coverage?.display_label

  // Onboarding state to a readable status
  const careStatus =
    (() => {
      switch (true) {
        // Deactivated status takes precedence over all other conditions
        case onboardingStatus === ONBOARDING_STATES.deactivated:
          return 'Deactivated'
        case CurrentPrimaryCareEnrollment === null &&
          onboardingStatus === ONBOARDING_STATES.signedup:
          return 'Signed up'
        case CurrentPrimaryCareEnrollment === null:
          return 'None'
        case CurrentPrimaryCareEnrollment?.status === PRIMARY_CARE_STATUS.ESTABLISHED &&
          CurrentPrimaryCareEnrollment?.reason?.includes(DISCHARGE_IN_PROGRESS_TEXT):
          return 'Discharge in progress'
        case CurrentPrimaryCareEnrollment?.status === PRIMARY_CARE_STATUS.CHURNED &&
          CurrentPrimaryCareEnrollment?.reason &&
          CurrentPrimaryCareEnrollment?.reason.split(':')[0] === 'Discharged':
          return 'Discharged'
        case CurrentPrimaryCareEnrollment?.status === PRIMARY_CARE_STATUS.CHURNED:
          return 'Churned'
        // Every non-churned status shows the plain "Care" chip: this chip identifies the
        // program the member is in, not their enrollment status — the status renders in the
        // header status line and the Programs-tab pill.
        case CurrentPrimaryCareEnrollment?.status !== PRIMARY_CARE_STATUS.CHURNED:
          return 'Care'
        default:
          return 'None'
      }
    })() || 'None'

  return (
    <>
      {careStatus != 'None' && (
        <Chip
          size="small"
          variant={careChipVariant}
          label={careStatus}
          icon={CareIcon}
          title={`Primary Care: ${careStatus}`}
          style={{ marginRight: '0.3rem' }}
        />
      )}
      {coverageStatus != 'None' && (
        <Badge
          badgeContent=" "
          slotProps={{ badge: { sx: { bgcolor: '#ffd713', color: '#ffd713' } } }}
          invisible={coverageEndBadge}
          overlap="circular"
          variant="dot"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Chip
            size="small"
            variant={coverageChipVariant}
            label={coverageStatus}
            icon={CoverageIcon}
            title={`Coverage: ${coverageStatus}${coverageEndTooltip}`}
            color="default"
          />
        </Badge>
      )}
    </>
  )
}

export default PrimaryCareChip
